/*
 * @nu-art/logger - Flexible logging infrastructure with multiple output targets
 * Copyright (C) 2026 Adam van der Kruk aka TacB0sS
 * Licensed under the Apache License, Version 2.0
 */
/**
 * Central logging manager that distributes log messages to all registered log clients.
 *
 * BeLogged acts as a dispatcher - when a log message is sent, it forwards it to all
 * registered LogClient instances. This allows multiple output destinations simultaneously
 * (e.g., console + file + remote logging).
 *
 * Log clients are added via `addClient()` and can be removed via `removeClient()`.
 * Each client can have its own filter and prefix composer.
 */
class BeLogged_Class {
    /** Array of registered log clients */
    clients = [];
    /** Line count for console rewriting (used by some clients) */
    lineCount = 0;
    /**
     * Registers a log client to receive all log messages.
     *
     * The client's `init()` method is called immediately. If the client is already
     * registered, this method does nothing (no duplicate registration).
     *
     * @param client - LogClient instance to register
     */
    addClient(client) {
        if (this.clients.indexOf(client) !== -1)
            return;
        client.init();
        this.clients.push(client);
    }
    /**
     * Removes a log client (alias for removeClient for backwards compatibility).
     *
     * @param client - LogClient instance to remove
     */
    removeConsole(client) {
        this.removeClient(client);
    }
    /**
     * Removes a log client from the registry.
     *
     * The client's `stop()` method is called for cleanup. If the client is not
     * registered, this method does nothing.
     *
     * @param client - LogClient instance to remove
     */
    removeClient(client) {
        const index = this.clients.indexOf(client);
        if (index === -1)
            return;
        this.clients.splice(index, 1);
        client.stop();
    }
    /**
     * Logs a message to all registered clients.
     *
     * @param tag - Logger tag/identifier
     * @param level - Log level
     * @param bold - Whether to apply bold formatting
     * @param toLog - Values to log (spread arguments)
     */
    log(tag, level, bold, ...toLog) {
        this.logImpl(tag, level, bold, toLog);
    }
    /**
     * Internal implementation that distributes the log message to all clients.
     *
     * Creates a copy of the toLog array for each client to prevent mutation issues.
     *
     * @param tag - Logger tag/identifier
     * @param level - Log level
     * @param bold - Whether to apply bold formatting
     * @param toLog - Array of values to log
     */
    logImpl(tag, level, bold, toLog) {
        for (const client of this.clients) {
            client.log(tag, level, bold, [...toLog]);
        }
    }
    /**
     * Clears footer lines from console output.
     *
     * Used by console-based log clients to clear previously written footer content.
     * Writes newlines to stdout to clear the specified number of lines.
     */
    clearFooter() {
        for (let i = 0; i < this.lineCount + 3; i++) {
            process.stdout.write(`\n`);
        }
    }
    /**
     * Sets the line count for console rewriting operations.
     *
     * Used by log clients that rewrite console output (e.g., progress indicators).
     *
     * @param lineCount - Number of lines to track for rewriting
     */
    rewriteConsole(lineCount) {
        this.lineCount = lineCount;
    }
    /**
     * Removes all registered log clients and stops them.
     *
     * Useful for test cleanup to ensure no clients remain active.
     */
    removeAllClients() {
        const clients = [...this.clients];
        for (const client of clients) {
            this.removeClient(client);
        }
    }
}
/**
 * Singleton instance of the logging manager.
 *
 * This is the central entry point for all logging. Logger instances call
 * `BeLogged.logImpl()` to output messages, which are then distributed to
 * all registered log clients.
 */
export const BeLogged = new BeLogged_Class();
//# sourceMappingURL=BeLogged.js.map